import { createSignal, Show, type Component } from "solid-js";

const MAX_LEN = 10_000;

export interface CommentInputProps {
  onSubmit: (body: string) => Promise<void>;
}

export const CommentInput: Component<CommentInputProps> = (props) => {
  const [draft, setDraft] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const isValid = () => {
    const len = draft().length;
    return len >= 1 && len <= MAX_LEN;
  };

  const isDisabled = () => !isValid() || isSubmitting();

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (isDisabled()) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await props.onSubmit(draft());
      setDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to post comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} class="flex flex-col gap-2">
      <textarea
        class="min-h-[80px] w-full resize-y rounded-md border border-line p-2 text-sm"
        placeholder="Write a comment (markdown supported)…"
        value={draft()}
        onInput={(e) => setDraft(e.currentTarget.value)}
        disabled={isSubmitting()}
      />
      <Show when={error()}>
        <p class="text-sm text-signal-red">{error()}: failed to post</p>
      </Show>
      <div class="flex items-center justify-between">
        <span class="text-xs text-ink-3">
          {draft().length} / {MAX_LEN}
        </span>
        <button
          type="submit"
          disabled={isDisabled()}
          class="rounded-md bg-ink px-3 py-1.5 text-sm font-medium text-paper hover:bg-ink-2 focus-ring disabled:cursor-not-allowed"
        >
          Submit
        </button>
      </div>
    </form>
  );
};
